<script>
import { apiSecond } from '@/boot/axios'
import { numberFormaters } from '@/helpers'

import FishingYearSelector from '@/components/UI/FishingYearSelector.vue'
import HeaderBack from '@/components/UI/HeaderBack.vue'

// Цвет закреплён за кодом вида рыболовства из handbook_fish_type_catches
// (4 — промысловое, 5 — любительское, 6 — научно-исследовательский лов,
// 7 — экспериментальный, 8 — мелиоративный, 9 — в воспроизводственных целях),
// чтобы вид узнавался по цвету так же, как на «Моих орудиях лова».
const FISHING_TYPE_COLORS = {
	4: '#157FEE',
	5: '#00E096',
	6: '#9B8AFB',
	7: '#00B8D9',
	8: '#E0BB49',
	9: '#F64A4A',
}
// Вид промысла без кода — бэк сводит такие разрешения в одну строку.
const FISHING_TYPE_COLOR_DEFAULT = '#475F84'

const GEAR_COLOR = '#157FEE'
// Орудие не сопоставлено со справочником — приглушаем, чтобы полоса читалась
// как сырой текст eLicense, а не как полноценный тип.
const GEAR_COLOR_UNMATCHED = '#8494AE'
const GEAR_COLOR_REST = '#C3CEDF'

// Типов орудий в справочнике десятки, бэк отдаёт их все — срез топа делает фронт,
// иначе карточка растягивается на весь экран (так же в аналитике по таможне).
const TOP_GEARS_LIMIT = 10

// Дата выдачи лежит в univer_models.IssueDate, а сама колонка появилась 23.07.2024:
// у более ранних разрешений она пуста и в дашборд они не попадают вовсе.
const DATA_STARTS_AT = '2024-07-23'

function toNumber(value) {
	const num = Number(value)
	return Number.isFinite(num) ? num : 0
}

export default {
	name: 'GearsAnalytics',
	components: {
		FishingYearSelector,
		HeaderBack,
	},
	data() {
		return {
			// null — данных ещё не было, страницу закрывает прелоадер;
			// {} — загрузились, но разрешений в периоде нет.
			data: null,
			loading: false,
			period: this.$store.getters['dashboard/getSelectedFishingYear'],
			errorMessage: {
				status: null,
				text: null,
			},
		}
	},
	computed: {
		payload() {
			return this.data || {}
		},
		rawRegionsByType() {
			return Array.isArray(this.payload.regions_by_type) ? this.payload.regions_by_type : []
		},
		rawTopGears() {
			return Array.isArray(this.payload.top_gears) ? this.payload.top_gears : []
		},
		rawTopGearByRegion() {
			return Array.isArray(this.payload.top_gear_by_region) ? this.payload.top_gear_by_region : []
		},
		// Легенду и порядок сегментов считаем по стране. Внутри региона бэк сортирует
		// строки по убыванию, то есть у каждого региона порядок свой — если раскладывать
		// сегменты как пришло, полосы регионов не сравнить глазом.
		fishingTypeLegend() {
			const totals = new Map()

			this.rawRegionsByType.forEach((row) => {
				const key = this.fishingTypeKey(row)

				if (!totals.has(key)) {
					totals.set(key, {
						key,
						title: row.fishing_type || this.$t('gears_analytics.fishing_type_unknown'),
						color: FISHING_TYPE_COLORS[row.fishing_type_code] || FISHING_TYPE_COLOR_DEFAULT,
						total: 0,
					})
				}

				totals.get(key).total += toNumber(row.gear_count)
			})

			return [...totals.values()].sort((a, b) => b.total - a.total)
		},
		// Ответ плоский — строка на «регион × вид промысла». Порядок регионов из ответа
		// сохраняем: бэк уже отдал нужный (по названию, «Регион не определён» последним).
		regionRows() {
			const rows = []
			const indexByKey = new Map()

			this.rawRegionsByType.forEach((row) => {
				// Регион может быть не распознан — такие строки приходят с region_id: null
				// и должны схлопнуться в одну, а не в строку на каждый вид промысла.
				const key = row.region_id != null ? `id:${row.region_id}` : 'unknown'

				if (!indexByKey.has(key)) {
					indexByKey.set(key, rows.length)
					rows.push({
						key,
						title: row.region || this.$t('gears_analytics.region_unknown'),
						total: 0,
						unparsed: 0,
						byType: new Map(),
					})
				}

				const region = rows[indexByKey.get(key)]
				const count = toNumber(row.gear_count)

				region.total += count
				region.unparsed += toNumber(row.unparsed)
				region.byType.set(this.fishingTypeKey(row), { count, share: row.share })
			})

			return rows.map((region) => ({
				key: region.key,
				title: region.title,
				total: region.total,
				unparsed: region.unparsed,
				segments: this.fishingTypeLegend
					.map((type) => {
						const cell = region.byType.get(type.key)
						if (!cell || !cell.count) return null

						return {
							key: type.key,
							title: type.title,
							color: type.color,
							count: cell.count,
							share: cell.share != null ? toNumber(cell.share) : 0,
							// Ширину считаем от суммы региона, а не из share: он округлён
							// до одного знака и в сумме даёт 99,9–100,1 вместо ровных 100.
							width: region.total ? (cell.count / region.total) * 100 : 0,
						}
					})
					.filter(Boolean),
			}))
		},
		// Регионы — от большего к меньшему по количеству орудий (бэк отдаёт их по
		// названию). «Регион не определён» держим последним, как и бэк: это не регион,
		// а корзина неразобранных разрешений, и в середине ранжированного списка она
		// читалась бы как настоящий результат. Сортируем копию из map(), не источник.
		sortedRegionRows() {
			return [...this.regionRows].sort((a, b) => {
				if (a.key === 'unknown') return 1
				if (b.key === 'unknown') return -1
				return b.total - a.total
			})
		},
		topGearRows() {
			const rows = this.rawTopGears.map((row) => ({
				// Сопоставленные орудия уникальны по id справочника, нераспознанные —
				// по сырому тексту eLicense, по которому их и группирует бэк.
				key: row.gear_id != null ? `gear:${row.gear_id}` : `raw:${row.title}`,
				title: row.title || this.$t('gears_analytics.gear_type_unknown'),
				count: toNumber(row.gear_count),
				share: toNumber(row.share),
				unparsed: toNumber(row.unparsed),
				matched: row.gear_id != null,
				rest: false,
			}))

			if (rows.length <= TOP_GEARS_LIMIT) return this.withBarWidths(rows)

			const head = rows.slice(0, TOP_GEARS_LIMIT)
			const tail = rows.slice(TOP_GEARS_LIMIT)

			head.push({
				key: 'rest',
				title: this.$t('gears_analytics.other_gears', [tail.length]),
				count: tail.reduce((sum, row) => sum + row.count, 0),
				share: tail.reduce((sum, row) => sum + row.share, 0),
				unparsed: tail.reduce((sum, row) => sum + row.unparsed, 0),
				matched: true,
				rest: true,
			})

			return this.withBarWidths(head)
		},
		topGearByRegionRows() {
			return this.rawTopGearByRegion.map((row) => ({
				// Строка на регион, но ключ берём вместе с орудием: при равенстве бэк
				// оставляет одно, и если правило когда-то изменится, ключи не столкнутся.
				key: `${row.region_id != null ? `id:${row.region_id}` : 'unknown'}|${
					row.gear_id != null ? `gear:${row.gear_id}` : `raw:${row.title}`
				}`,
				region: row.region || this.$t('gears_analytics.region_unknown'),
				title: row.title || this.$t('gears_analytics.gear_type_unknown'),
				matched: row.gear_id != null,
				count: toNumber(row.gear_count),
				regionTotal: toNumber(row.region_total),
				share: toNumber(row.share),
			}))
		},
		// Сумма по типам орудий — это все орудия страны за период: в top_gears бэк отдаёт
		// полный список типов, а не срез.
		totalGears() {
			return this.rawTopGears.reduce((sum, row) => sum + toNumber(row.gear_count), 0)
		},
		hasUnparsed() {
			return (
				this.regionRows.some((row) => row.unparsed > 0) ||
				this.topGearRows.some((row) => row.unparsed > 0)
			)
		},
		hasAnyData() {
			return Boolean(this.regionRows.length || this.topGearRows.length)
		},
		// Селектор периода начинается с рыболовного года 2024/2025, а дат выдачи
		// до 23.07.2024 в базе нет — предупреждаем, чтобы провал не выглядел сбоем.
		showHistoryHint() {
			return Boolean(this.period?.start) && this.period.start < DATA_STARTS_AT
		},
	},
	watch: {
		// Регионы, виды промысла и названия орудий приходят локализованными с бэка,
		// а язык в шапке переключается без перезагрузки страницы — перезапрашиваем.
		'$i18n.locale'() {
			this.fetchDashboard()
		},
	},
	beforeCreate() {
		if (!localStorage.token) {
			this.$router.push('/ru/login')
		}
	},
	mounted() {
		this.fetchDashboard()
	},
	head: {
		title() {
			return {
				inner: this.$t('sidebar.gears_analytics'),
			}
		},
		meta: [],
	},
	methods: {
		// Кода вида промысла в разрешении может не быть — такие строки различаем
		// по названию, иначе все они слились бы в один сегмент.
		fishingTypeKey(row) {
			return row.fishing_type_code != null
				? `code:${row.fishing_type_code}`
				: `title:${row.fishing_type ?? ''}`
		},
		withBarWidths(rows) {
			const max = rows.reduce((top, row) => Math.max(top, row.count), 0)

			return rows.map((row) => ({
				...row,
				color: row.rest ? GEAR_COLOR_REST : row.matched ? GEAR_COLOR : GEAR_COLOR_UNMATCHED,
				width: max ? (row.count / max) * 100 : 0,
			}))
		},
		formatCount(count) {
			if (count == null) return '—'
			return numberFormaters(count)
		},
		formatShare(share) {
			if (share == null) return '—'
			return new Intl.NumberFormat('ru-RU', {
				minimumFractionDigits: 1,
				maximumFractionDigits: 1,
			}).format(toNumber(share))
		},
		segmentTooltip(segment) {
			return `${segment.title}: ${this.formatCount(segment.count)} — ${this.formatShare(
				segment.share,
			)}%`
		},
		gearTooltip(gear) {
			return `${this.formatCount(gear.count)} — ${this.formatShare(gear.share)}%`
		},
		handlePeriodChange(period) {
			this.period = period
			// Период рыболовства общий для кабинета — держим его там же, где дашборд.
			this.$store.commit('dashboard/SET_SELECTED_FISHING_YEAR', period)
			this.fetchDashboard()
		},
		async fetchDashboard() {
			this.loading = true
			this.errorMessage = { status: null, text: null }

			try {
				const response = await apiSecond.get('v2/dashboard/permits', {
					// Период считается по дате выдачи разрешения (issue_date), а не по сроку
					// действия — этим блоки и отличаются от «Моих орудий лова».
					// granularity и region_id не передаём: они перестраивают только блок
					// dynamics, а его на этой странице нет.
					params: {
						date_from: this.period?.start,
						date_to: this.period?.finish,
					},
					// Аксессоры HandbookRegion и HandbookFishTypeCatch сравнивают локаль
					// с 'kk', а фронт живёт на 'kz' — без подмены казахский пользователь
					// получил бы русские названия регионов и видов промысла.
					headers: {
						'Accept-Language': this.$i18n.locale === 'kz' ? 'kk' : this.$i18n.locale,
					},
				})

				const body = response?.data ?? {}
				// Спецификация показывает блоки в корне ответа; на случай стандартного
				// конверта Laravel разворачиваем и `{ data: {...} }`.
				this.data = body.regions_by_type || body.top_gears || body.filters ? body : body.data ?? {}
			} catch (error) {
				// data не трогаем: первая загрузка — ошибку покажет прелоадер, повторная —
				// блоки остаются на экране, а ошибка уходит в баннер.
				this.checkError(error)
			} finally {
				this.loading = false
			}
		},
		checkError(error) {
			const status = error?.response?.status

			if (status >= 500) {
				this.errorMessage.status = 500
				this.errorMessage.text = this.$t('system_message.500')
			} else if (status === 401) {
				this.errorMessage.status = 401
				this.errorMessage.text = error?.response?.data?.message
				this.$router.push('/' + this.$i18n.locale + '/login')
			} else if (status === 403) {
				this.errorMessage.status = 403
				this.errorMessage.text = this.$t('gears_analytics.forbidden')
			} else {
				this.errorMessage.status = status || 422
				this.errorMessage.text = error?.response?.data?.message || this.$t('system_message.500')
			}
		},
	},
}
</script>

<template>
	<div
		class="wrap__content gears-analytics"
		v-bind:class="{ 'wrap__content--preloader': !$store.state.userObject || data === null }"
	>
		<v-preloader
			v-if="!$store.state.userObject || data === null"
			:message="errorMessage"
		></v-preloader>

		<div class="container" v-if="$store.state.userObject && data !== null">
			<div class="row">
				<div class="col-xl-2 col-lg-3">
					<v-sidebar :active="['GearsAnalytics']"></v-sidebar>
				</div>
				<div class="col-xl-10 col-lg-9">
					<div class="content-wrapper">
						<div class="card__content">
							<HeaderBack :title="$t('sidebar.gears_analytics')" />

							<div class="card__content--body">
								<div class="flex flex-wrap items-end justify-between gap-[16px] mb-[20px]">
									<div class="max-w-[620px]">
										<div class="font-main font-normal text-[12px] text-second leading-[1.5]">
											{{ $t('gears_analytics.hint') }}
										</div>
										<div
											v-if="showHistoryHint"
											class="font-main font-normal text-[12px] text-[#8A5B12] leading-[1.5] mt-[4px]"
										>
											{{ $t('gears_analytics.history_hint') }}
										</div>
									</div>
									<FishingYearSelector
										:model-value="period"
										:title="$t('admin.hello.fishing_year')"
										:disabled="loading"
										@change="handlePeriodChange"
									/>
								</div>

								<div
									v-if="errorMessage.text"
									class="form__block--line form__messages form__messages--error"
								>
									<label class="form__block--input">
										<span class="form__block--title">
											{{ errorMessage.text }}
										</span>
									</label>
								</div>

								<div :class="{ 'ga-board--loading': loading }">
									<template v-if="hasAnyData">
										<div class="ga-stats">
											<div class="ga-stat">
												<div class="ga-stat__label">
													{{ $t('gears_analytics.stat_total_gears') }}
												</div>
												<div class="ga-stat__value">{{ formatCount(totalGears) }}</div>
											</div>
											<div class="ga-stat">
												<div class="ga-stat__label">{{ $t('gears_analytics.stat_regions') }}</div>
												<div class="ga-stat__value">{{ formatCount(regionRows.length) }}</div>
											</div>
											<div class="ga-stat">
												<div class="ga-stat__label">
													{{ $t('gears_analytics.stat_gear_types') }}
												</div>
												<div class="ga-stat__value">{{ formatCount(rawTopGears.length) }}</div>
											</div>
										</div>

										<!-- Блок 1 — regions_by_type -->
										<div class="ga-card">
											<div class="ga-card__title">{{ $t('gears_analytics.regions_title') }}</div>
											<div class="ga-card__hint">{{ $t('gears_analytics.regions_hint') }}</div>

											<template v-if="regionRows.length">
												<div class="ga-legend">
													<span
														v-for="type in fishingTypeLegend"
														:key="type.key"
														class="ga-legend__item"
													>
														<i class="ga-legend__dot" :style="{ background: type.color }"></i>
														{{ type.title }}
													</span>
												</div>

												<div class="ga-rows">
													<div v-for="region in sortedRegionRows" :key="region.key" class="ga-row">
														<div class="ga-row__label">{{ region.title }}</div>
														<div class="ga-track">
															<div
																v-for="segment in region.segments"
																:key="region.key + '|' + segment.key"
																class="ga-seg"
																:style="{ width: segment.width + '%', background: segment.color }"
																v-tooltip="segmentTooltip(segment)"
															></div>
														</div>
														<div class="ga-row__total">
															{{ formatCount(region.total) }}
															<span
																v-if="region.unparsed"
																class="ga-warn"
																v-tooltip="$t('gears_analytics.unparsed', [region.unparsed])"
															>
																!
															</span>
														</div>
													</div>
												</div>
											</template>
											<div v-else class="ga-empty">{{ $t('gears_analytics.no_data') }}</div>
										</div>

										<!-- Блок 3 — top_gears -->
										<div class="ga-card">
											<div class="ga-card__title">{{ $t('gears_analytics.top_gears_title') }}</div>
											<div class="ga-card__hint">{{ $t('gears_analytics.top_gears_hint') }}</div>

											<template v-if="topGearRows.length">
												<div class="ga-rows">
													<div v-for="gear in topGearRows" :key="gear.key" class="ga-row">
														<div class="ga-row__label">
															{{ gear.title }}
															<span v-if="!gear.matched" class="ga-row__note">
																{{ $t('gears_analytics.not_matched') }}
															</span>
														</div>
														<div class="ga-track">
															<div
																class="ga-seg"
																:style="{ width: gear.width + '%', background: gear.color }"
																v-tooltip="gearTooltip(gear)"
															></div>
														</div>
														<div class="ga-row__total">
															{{ formatCount(gear.count) }}
															<span class="ga-row__share">{{ formatShare(gear.share) }}%</span>
															<span
																v-if="gear.unparsed"
																class="ga-warn"
																v-tooltip="$t('gears_analytics.unparsed', [gear.unparsed])"
															>
																!
															</span>
														</div>
													</div>
												</div>
											</template>
											<div v-else class="ga-empty">{{ $t('gears_analytics.no_data') }}</div>
										</div>

										<!-- Блок 4 — top_gear_by_region -->
										<div class="ga-card">
											<div class="ga-card__title">
												{{ $t('gears_analytics.top_by_region_title') }}
											</div>
											<div class="ga-card__hint">
												{{ $t('gears_analytics.top_by_region_hint') }}
											</div>

											<div v-if="topGearByRegionRows.length" class="ga-table">
												<div class="overflow-x-auto">
													<table class="w-full border-collapse font-main text-[13px]">
														<thead>
															<tr class="bg-[#F7F9FC]">
																<th class="ga-th">{{ $t('gears_analytics.th_region') }}</th>
																<th class="ga-th">{{ $t('gears_analytics.th_gear') }}</th>
																<th class="ga-th w-[120px]">
																	{{ $t('gears_analytics.th_count') }}
																</th>
																<th class="ga-th w-[150px]">
																	{{ $t('gears_analytics.th_share') }}
																</th>
															</tr>
														</thead>
														<tbody>
															<tr
																v-for="row in topGearByRegionRows"
																:key="row.key"
																class="border-t border-[#EEF1F6] first:border-t-0"
															>
																<td class="ga-td text-third">{{ row.region }}</td>
																<td class="ga-td text-third">
																	<div>{{ row.title }}</div>
																	<div v-if="!row.matched" class="ga-td__note">
																		{{ $t('gears_analytics.not_matched') }}
																	</div>
																</td>
																<td class="ga-td font-semibold text-third whitespace-nowrap">
																	{{ formatCount(row.count) }}
																</td>
																<td class="ga-td whitespace-nowrap">
																	<span class="font-semibold text-third">
																		{{ formatShare(row.share) }}%
																	</span>
																	<span class="ga-td__note">
																		{{
																			$t('gears_analytics.of_region_total', [
																				formatCount(row.regionTotal),
																			])
																		}}
																	</span>
																</td>
															</tr>
														</tbody>
													</table>
												</div>
											</div>
											<div v-else class="ga-empty">{{ $t('gears_analytics.no_data') }}</div>
										</div>

										<div v-if="hasUnparsed" class="ga-footnote">
											{{ $t('gears_analytics.unparsed_note') }}
										</div>
									</template>

									<div
										v-else
										class="flex flex-col items-center justify-center py-[48px] text-center"
									>
										<div class="font-main font-medium text-[16px] text-fourth leading-[1.5]">
											{{ $t('gears_analytics.empty') }}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
/* Пока идёт перезапрос, блоки остаются на экране, но приглушены. */
.ga-board--loading {
	opacity: 0.55;
	pointer-events: none;
	transition: opacity 0.2s;
}

.ga-stats {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
	gap: 16px;
	margin-bottom: 24px;
}

.ga-stat,
.ga-card {
	background: #ffffff;
	border: 1px solid #eef1f6;
	border-radius: 10px;
	box-shadow: 0 8px 30px rgba(210, 232, 254, 0.25);
}

.ga-stat {
	padding: 16px 20px;
}

.ga-stat__label {
	font-family: 'Geologica', sans-serif;
	font-size: 13px;
	line-height: 1.35;
	color: #425166;
}

.ga-stat__value {
	font-family: 'Geologica', sans-serif;
	font-weight: 700;
	font-size: 26px;
	line-height: 1.3;
	color: #151d48;
	margin-top: 4px;
}

.ga-card {
	padding: 20px 24px;
	margin-bottom: 24px;
}

.ga-card__title {
	font-family: 'Geologica', sans-serif;
	font-weight: 600;
	font-size: 16px;
	line-height: 1.35;
	color: #151d48;
}

.ga-card__hint {
	font-family: 'Geologica', sans-serif;
	font-size: 12px;
	line-height: 1.5;
	color: #8a93a6;
	margin-top: 2px;
}

.ga-legend {
	display: flex;
	flex-wrap: wrap;
	gap: 8px 20px;
	margin-top: 16px;
}

.ga-legend__item {
	display: inline-flex;
	align-items: center;
	font-family: 'Geologica', sans-serif;
	font-size: 12px;
	line-height: 1.35;
	color: #425166;
}

.ga-legend__dot {
	width: 9px;
	height: 9px;
	border-radius: 2px;
	margin-right: 7px;
	flex-shrink: 0;
}

.ga-rows {
	margin-top: 18px;
}

/* Подпись занимает всю ширину, пока не хватает места: на узком экране полоса
   уезжает под название региона, а не сжимается в несколько пикселей. */
.ga-row {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px 12px;
	margin-bottom: 14px;
}

.ga-row:last-child {
	margin-bottom: 0;
}

.ga-row__label {
	width: 100%;
	font-family: 'Geologica', sans-serif;
	font-size: 13px;
	line-height: 1.35;
	color: #151d48;
}

.ga-row__note {
	display: block;
	font-size: 11px;
	line-height: 1.4;
	color: #8a93a6;
	margin-top: 2px;
}

.ga-track {
	flex: 1 1 180px;
	display: flex;
	height: 22px;
	border-radius: 5px;
	overflow: hidden;
	background: #f1f3f8;
}

.ga-seg {
	height: 100%;
	transition: width 0.3s ease;
}

.ga-row__total {
	margin-left: auto;
	min-width: 74px;
	text-align: right;
	font-family: 'Geologica', sans-serif;
	font-weight: 600;
	font-size: 13px;
	line-height: 1.35;
	color: #151d48;
	white-space: nowrap;
}

.ga-row__share {
	font-weight: 400;
	color: #8a93a6;
	margin-left: 6px;
}

.ga-warn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 16px;
	height: 16px;
	margin-left: 6px;
	border-radius: 50%;
	background: #fcf0dc;
	color: #8a5b12;
	font-size: 11px;
	font-weight: 700;
	line-height: 1;
	cursor: help;
	vertical-align: middle;
}

@media (min-width: 768px) {
	.ga-row__label {
		width: 240px;
		flex-shrink: 0;
	}
}

.ga-table {
	margin-top: 16px;
	border: 1px solid #e3e7ef;
	border-radius: 10px;
	overflow: hidden;
}

.ga-th {
	text-align: left;
	padding: 10px 14px;
	font-weight: 600;
	color: #6b7280;
	border-bottom: 1px solid #e3e7ef;
}

.ga-td {
	padding: 10px 14px;
	vertical-align: top;
}

.ga-td__note {
	display: block;
	font-size: 11px;
	line-height: 1.4;
	color: #8a93a6;
	margin-top: 3px;
}

.ga-empty {
	font-family: 'Geologica', sans-serif;
	font-size: 13px;
	line-height: 1.5;
	color: #8a93a6;
	padding: 24px 0 8px;
	text-align: center;
}

.ga-footnote {
	font-family: 'Geologica', sans-serif;
	font-size: 12px;
	line-height: 1.5;
	color: #8a93a6;
}
</style>
